import { ReactNode } from 'react';

import { Chip } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';

import { RiCloseLine as CloseIcon } from '@remixicon/react';

import { Focusable } from '@spotify-internal/encore-web';
import { Tooltip, TooltipRoot } from '@spotify-internal/encore-web/next';

import { FilterViewer } from './FilterViewer';
import { useFilterChipStyles } from './filterChipStyles';
import { FilterViewerComponentProps } from './provider';

export interface FilterChipProps<T> extends FilterViewerComponentProps<T> {
  label: ReactNode;
  loading?: boolean;
  tooltip?: ReactNode;
}

export const FilterChip = <T,>(props: FilterChipProps<T>) => {
  const { label, loading, tooltip, ...rest } = props;
  const classes = useFilterChipStyles();

  if (loading) {
    return <ChipSkeleton />;
  }

  return (
    <FilterViewer {...rest}>
      {({ onRemove, onEdit }) => {
        const chip = (
          <Chip
            className={classes.focusRing}
            label={label}
            onDelete={onRemove}
            onClick={onEdit}
            deleteIcon={<CloseIcon />}
          />
        );

        if (!tooltip) return chip;

        return (
          <TooltipRoot>
            <Focusable>{chip}</Focusable>
            <Tooltip>{tooltip}</Tooltip>
          </TooltipRoot>
        );
      }}
    </FilterViewer>
  );
};

function ChipSkeleton() {
  return (
    <Skeleton
      variant="rect"
      style={{ borderRadius: 99999 }}
      data-testid="filter-chip-skeleton"
    >
      <Chip label="Loading..." deleteIcon={<CloseIcon />} />
    </Skeleton>
  );
}
